/**
 * Política de repetição do download dos documentos da NP.
 *
 * Nada é enviado por e-mail: o ZIP é o único caminho para o CNAB, o termo de
 * cessão e a NP. Uma queda de rede não pode custar o documento, então falhas
 * transitórias são repetidas antes de desistir e pedir uma nova tentativa.
 */
export const TENTATIVAS_DOWNLOAD = 3;

/** Dispara o download no browser a partir da URL assinada. */
export function dispararDownload(url: string, fileName: string): void {
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener";
  a.click();
}

/** Espera crescente entre as tentativas, com teto de 8s. */
export const esperaDownload = (tentativa: number): number =>
  Math.min(1000 * 2 ** tentativa, 8000);

export function deveRepetirDownload(tentativas: number, error: unknown): boolean {
  if (tentativas >= TENTATIVAS_DOWNLOAD) return false;

  const status = (error as { response?: { status?: number } })?.response?.status;
  // Sem status = rede caiu ou deu timeout antes de chegar resposta.
  if (status === undefined) return true;
  // 4xx é definitivo, inclusive o 409 de "ainda em geração": repetir em
  // milissegundos não muda o resultado, quem espera é o polling da tela.
  return status >= 500;
}

/**
 * A geração dos documentos é assíncrona (SQS -> imagem R) e a listagem da tela
 * não tem refetch periódico: sem esperar, o pedido era enfileirado, a lista era
 * invalidada no mesmo instante — antes dos arquivos existirem — e nada mais
 * atualizava. Os ícones ficavam vermelhos e um download logo em seguida vinha
 * incompleto, em silêncio.
 */
export const INTERVALO_DOCS_MS = 3000;

/** CNAB de baixa: só o arquivo texto, ~10s com cold start (8,8s só de init). */
export const TENTATIVAS_BAIXA = 8;

/**
 * Documentos de compra: CNAB de remessa mais duas chamadas ao bookbinder (termo
 * de cessão e cártula). Mesma janela de ~4min que a tela de criação já espera.
 */
export const TENTATIVAS_COMPRA = 80;

const dormirPadrao = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

interface OpcoesEspera {
  tentativas?: number;
  intervaloMs?: number;
  dormir?: (ms: number) => Promise<void>;
}

/**
 * Espera até que todas as `total` NPs satisfaçam `pronto`.
 * true quando ficaram prontas; false se esgotou a espera.
 */
export async function esperarDocumentos<T>(
  buscarNotas: () => Promise<T[]>,
  total: number,
  pronto: (nota: T) => boolean,
  opts: OpcoesEspera = {}
): Promise<boolean> {
  const {
    tentativas = TENTATIVAS_BAIXA,
    intervaloMs = INTERVALO_DOCS_MS,
    dormir = dormirPadrao,
  } = opts;

  for (let tentativa = 0; tentativa < tentativas; tentativa++) {
    await dormir(intervaloMs);
    const notas = await buscarNotas();
    if (notas.length === total && notas.every(pronto)) {
      return true;
    }
  }
  return false;
}
